import { useLiveQuery } from 'dexie-react-hooks'
import { useCallback, useEffect } from 'react'
import { db, type CrmMapping } from '../lib/db'
import {
  matchCrmContact,
  setCrmMapping,
  skipCrmMapping,
  type CrmName,
  type MatchProfileInput,
} from '../lib/crm'

// The extension's service worker writes CrmMapping rows via raw IDB, which
// does NOT fire Dexie change events in the web app's page context. Mirror
// returned mappings into Dexie so useLiveQuery subscribers here notice.
async function mirrorMappingLocally(mapping: CrmMapping | null | undefined) {
  if (!mapping || !mapping.profileUrn || !mapping.crmName) return
  try {
    await db.crmMappings.put(mapping)
  } catch (e) {
    console.warn('[CRM] Failed to mirror mapping to Dexie:', e)
  }
}

export function useCrmMapping(profileUrn: string | null, crmName: CrmName | null) {
  // Defensive normalization — old IDB data might still have bare IDs
  // ("ACoAAB...") from before the parse-time normalization fix.
  const normalizedUrn = profileUrn && !profileUrn.startsWith('urn:li:')
    ? `urn:li:fsd_profile:${profileUrn}`
    : profileUrn

  const mapping = useLiveQuery<CrmMapping | undefined>(
    () => (normalizedUrn && crmName ? db.crmMappings.get([normalizedUrn, crmName]) : undefined),
    [normalizedUrn, crmName],
  )

  useEffect(() => {
    if (!normalizedUrn || !crmName) return
    const handler = (event: MessageEvent) => {
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'CRM_MAPPING_CHANGED') return
      const incoming = event.data.mapping as CrmMapping | null | undefined
      if (!incoming) return
      if (incoming.profileUrn !== normalizedUrn) return
      if (incoming.crmName !== crmName) return
      void mirrorMappingLocally(incoming)
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [normalizedUrn, crmName])

  const match = useCallback(
    async (profile: MatchProfileInput) => {
      if (!crmName) throw new Error('No CRM selected')
      const result = await matchCrmContact(crmName, profile)
      await mirrorMappingLocally(result)
      return result
    },
    [crmName],
  )

  const pickCandidate = useCallback(
    async (crmRecordId: string, crmContactName: string, crmContactEmail?: string | null) => {
      if (!normalizedUrn || !crmName) throw new Error('Missing profileUrn or crmName')
      const result = await setCrmMapping({
        profileUrn: normalizedUrn,
        crmName,
        crmRecordId,
        crmContactName,
        crmContactEmail: crmContactEmail ?? null,
      })
      await mirrorMappingLocally(result)
      return result
    },
    [normalizedUrn, crmName],
  )

  const skip = useCallback(
    async (displayName?: string) => {
      if (!normalizedUrn || !crmName) throw new Error('Missing profileUrn or crmName')
      const result = await skipCrmMapping({ profileUrn: normalizedUrn, crmName, displayName })
      await mirrorMappingLocally(result)
      return result
    },
    [normalizedUrn, crmName],
  )

  return { mapping, match, pickCandidate, skip }
}

// Returns the first connected CRM (for now, just affinity).
export function useActiveCrm(connections: { affinity: unknown | null }): CrmName | null {
  if (connections.affinity) return 'affinity'
  return null
}
